import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_get_actual_schedule, API_get_events, API_get_holidays, API_get_published_shift_count } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import { getCardColor2 } from "../../../../../helpers/helpers";
import LoadSpinner from "../../../../../utilities/LoadSpinner";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ShiftInputsModal from "../ShiftInputsModal";
import WeatherEventsCard from "../WeatherEventsCard";
import CopilotShiftCard from "./CopilotShiftCard";
import { getRoleNamesInDepartments } from "../departmentFilter";

export default function MonthlySchedule(props) {

  const [scheduleData, setScheduleData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [holidaysData, setHolidaysData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);
  const [monthlyPublishedCount, setMonthlyPublishedCount] = useState(0);

  // Track accepted copilot shifts (by UUID) so we can hide them after acceptance
  const [acceptedCopilotShifts, setAcceptedCopilotShifts] = useState(new Set());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // New shift modal state (for adding shifts without pre-selected person)
  const [newShiftModalOpen, setNewShiftModalOpen] = useState(false);
  const [newShiftDate, setNewShiftDate] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  // Edit shift modal state (for employee selection in monthly view)
  const [localSelectedEmployeeId, setLocalSelectedEmployeeId] = useState(null);

  const departmentFilterActive =
    Array.isArray(props.roleType) &&
    Array.isArray(props.roleTypeOptions) &&
    props.roleType.length < props.roleTypeOptions.length;
  const newShiftRoleNames = getRoleNamesInDepartments(
    props.roleType,
    props.all_data?.role_names,
    departmentFilterActive
  );

  // Notes drawer state
  const [notesDrawerState, setNotesDrawerState] = useState({
    open: false,
    dateStr: null,
  });

  const openNotesForDate = (dateStr) => {
    setNotesDrawerState({ open: true, dateStr });
  };

  const closeNotesDrawer = () => {
    setNotesDrawerState({ open: false, dateStr: null });
  };

  const setNotesDrawerOpen = (isOpen, dateStr) => {
    setNotesDrawerState({
      open: isOpen,
      dateStr: isOpen ? dateStr : null,
    });
  };

  // Use props for month/year instead of internal state
  const currentMonth = props.currentMonth;
  const currentYear = props.currentYear;

  const axiosConfig = getDefaultAxiosConfig();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const allDaysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Reorder days of week based on business week start day
  const businessWeekStartDayIndex = props.businessWeekStartDayIndex || 0;
  const daysOfWeek = [
    ...allDaysOfWeek.slice(businessWeekStartDayIndex),
    ...allDaysOfWeek.slice(0, businessWeekStartDayIndex)
  ];

  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  // Helper to get week start date from a day in the calendar
  const getWeekStartDate = (dayDate) => {
    // Parse date string as local date to avoid timezone issues
    const [year, month, day] = dayDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const dayOfWeek = date.getDay();
    const diff = (dayOfWeek - businessWeekStartDayIndex + 7) % 7;
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - diff);

    return weekStart;
  };

  // Check if this week's copilot is currently active
  const isWeekCopilotActive = (weekStartDate) => {
    if (!props.selectedWeekForMonthlyCopilot) return false;
    return formatDate(weekStartDate) === formatDate(props.selectedWeekForMonthlyCopilot);
  };

  // Handle copilot button click for a week
  const handleWeekCopilotClick = (weekStartDate) => {
    const isCurrentlyActive = isWeekCopilotActive(weekStartDate);

    if (isCurrentlyActive) {
      // Toggle off if clicking the same week
      props.setIsCopilotOn(false);
      props.setSelectedWeekForMonthlyCopilot(null);
      // Reset accepted shifts when turning off copilot
      setAcceptedCopilotShifts(new Set());
    } else {
      // Reset accepted shifts when changing weeks
      setAcceptedCopilotShifts(new Set());

      // Set this week as selected
      props.setSelectedWeekForMonthlyCopilot(weekStartDate);

      // Turn on copilot (or keep it on if already on for a different week)
      // The parent's isCopilotOn state being true will trigger the polling
      props.setIsCopilotOn(true);
    }
  };

  // Get copilot shifts for a specific day (if copilot is on and this day is in the selected week)
  const getCopilotShiftsForDay = (dateStr) => {
    if (!props.isCopilotOn || !props.selectedWeekForMonthlyCopilot || !props.all_data?.copilot_schedule) {
      return [];
    }

    // Check if this day is in the selected week
    const dayWeekStart = getWeekStartDate(dateStr);
    const selectedWeekStart = formatDate(props.selectedWeekForMonthlyCopilot);
    const dayWeekStartStr = formatDate(dayWeekStart);

    if (dayWeekStartStr !== selectedWeekStart) {
      return []; // This day is not in the selected week
    }

    // Return copilot shifts for this specific day
    const shifts = props.all_data.copilot_schedule.filter(shift => shift.date === dateStr) || [];
    console.log(`getCopilotShiftsForDay(${dateStr}):`, {
      totalCopilotShifts: props.all_data.copilot_schedule.length,
      shiftsForThisDay: shifts.length,
      shifts: shifts.map(s => ({
        uuid: s.uuid,
        person: `${s.first_name} ${s.last_name}`,
        time: `${s.shift_start}-${s.shift_end}`,
        is_copilot_suggestion: s.is_copilot_suggestion
      }))
    });
    return shifts;
  };

  // Fetch schedule data and events for the month including adjacent month days
  const fetchMonthlySchedule = async (showLoadingSpinner = true) => {
    if (showLoadingSpinner) {
      setLoading(true);
    }

    // Calculate the first day shown in the calendar (includes previous month days)
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    // Adjust for business week start day
    const daysFromPrevMonth = (firstDayOfWeek - businessWeekStartDayIndex + 7) % 7;
    const calendarStartDate = new Date(currentYear, currentMonth, 1 - daysFromPrevMonth);

    // Determine if we need 5 or 6 weeks
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const lastDayPosition = daysFromPrevMonth + daysInMonth - 1;
    const totalDays = lastDayPosition >= 35 ? 42 : 35;

    // Calculate the last day shown in the calendar (includes next month days)
    const calendarEndDate = new Date(calendarStartDate);
    calendarEndDate.setDate(calendarEndDate.getDate() + (totalDays - 1));

    const dateStart = formatDate(calendarStartDate);
    const dateEnd = formatDate(calendarEndDate);

    const scheduleUrl = `${API_get_actual_schedule}${props.store_id}&date_start=${dateStart}&date_end=${dateEnd}`;
    const eventsUrl = `${API_get_events}${props.store_id}&date_start=${dateStart}&date_end=${dateEnd}`;
    const holidaysUrl = `${API_get_holidays}?store_id=${props.store_id}&date_start=${dateStart}&date_end=${dateEnd}`;

    try {
      // Fetch schedule, events, and holidays in parallel
      const [scheduleResponse, eventsResponse, holidaysResponse] = await Promise.all([
        axios.get(scheduleUrl, axiosConfig),
        axios.get(eventsUrl, axiosConfig),
        axios.get(holidaysUrl, axiosConfig)
      ]);

      // Handle nested response structure for backward compatibility
      let scheduleArray = scheduleResponse.data;
      if (scheduleResponse.data?.redux?.payload?.actual_schedule_list) {
        scheduleArray = scheduleResponse.data.redux.payload.actual_schedule_list;
      }

      setScheduleData(scheduleArray || []);
      setEventsData(eventsResponse.data || []);
      setHolidaysData(holidaysResponse.data || []);

      // Fetch published shift count for the exact month range (not calendar padding)
      const monthStart = formatDate(new Date(currentYear, currentMonth, 1));
      const monthEnd = formatDate(new Date(currentYear, currentMonth + 1, 0));
      const publishedCountUrl = `${API_get_published_shift_count}${props.store_id}&date_start=${monthStart}&date_end=${monthEnd}`;
      try {
        const publishedCountResponse = await axios.get(publishedCountUrl, axiosConfig);
        const publishedCount = publishedCountResponse.data?.count_of_published_shifts || 0;
        setMonthlyPublishedCount(publishedCount);
      } catch (err) {
        console.error("Error fetching monthly published shift count:", err);
        setMonthlyPublishedCount(0);
      }
    } catch (error) {
      console.error("Error fetching monthly schedule:", error);
      setScheduleData([]);
      setEventsData([]);
      setHolidaysData([]);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (props.store_id) {
      fetchMonthlySchedule();
    }
  }, [currentMonth, currentYear, props.store_id, businessWeekStartDayIndex]);

  // Build calendar grid
  useEffect(() => {
    function isDateInCurrentMonth(date) {
      return (
        date.getMonth() === currentMonth && date.getFullYear() === currentYear
      );
    }

    const today = new Date();

    // Calculate the first day of the current month
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    // Calculate the number of days from the previous month to display
    // Adjust for business week start day
    const daysInPrevMonth = (firstDayOfMonth - businessWeekStartDayIndex + 7) % 7;

    // Determine if we need 5 or 6 weeks
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const lastDayPosition = daysInPrevMonth + daysInMonth - 1;
    const totalDays = lastDayPosition >= 35 ? 42 : 35;

    // Build the calendar days array
    const transformedData = [];
    const currentDate = new Date(currentYear, currentMonth, 1 - daysInPrevMonth);

    for (let i = 0; i < totalDays; i++) {
      const dateStr = formatDate(currentDate);
      const isInCurrentMonth = isDateInCurrentMonth(currentDate);

      // Find all shifts for this date and sort by start time
      const dayShifts = (scheduleData?.filter((shift) => shift.date === dateStr) || [])
        .sort((a, b) => a.shift_start - b.shift_start);

      // Find all events/notes for this date
      const dayEvents = eventsData?.filter((event) => event.date === dateStr) || [];

      // Find all holidays for this date
      const dayHolidays = holidaysData?.filter((holiday) => holiday.date === dateStr) || [];

      const isToday =
        currentDate.getFullYear() === today.getFullYear() &&
        currentDate.getMonth() === today.getMonth() &&
        currentDate.getDate() === today.getDate();

      transformedData.push({
        date: dateStr,
        dayNumber: currentDate.getDate(),
        shifts: dayShifts,
        events: dayEvents,
        holidays: dayHolidays,
        isCurrentMonth: isInCurrentMonth,
        isToday,
      });

      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setDays(transformedData);
  }, [currentMonth, currentYear, scheduleData, eventsData, holidaysData, businessWeekStartDayIndex]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  // Check if a date is in the past (excluding today)
  const isDateInPast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse date string (format: "YYYY-MM-DD") to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const shiftDate = new Date(year, month - 1, day); // month is 0-indexed
    shiftDate.setHours(0, 0, 0, 0);

    // Only return true if shift is strictly before today (not equal to today)
    return shiftDate.getTime() < today.getTime();
  };

  // Handle opening the shift modal
  const handleShiftClick = (shift, dateStr) => {
    // Don't allow clicking on optimistic shifts (still being saved)
    if (shift.id === 0) {
      return;
    }

    // Find the person info from store_team_member_weekly_info
    // Match by store_team_member ID (not user_id, since some employees may not have user accounts)
    const person = props.store_team_member_weekly_info?.find(
      (p) => p.id === shift.store_team_member
    );

    if (person) {
      // Create a deep copy of the shift to prevent modal state from being affected
      // by refetches that update scheduleData
      const shiftCopy = JSON.parse(JSON.stringify(shift));

      setSelectedShift(shiftCopy);
      setSelectedPerson(person);
      setSelectedDate(dateStr);

      // Initialize the employee selection for the edit modal
      setLocalSelectedEmployeeId(person.id);

      setModalOpen(true);
    }
  };

  // Handle closing the modal
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedShift(null);
    setSelectedPerson(null);
    setSelectedDate(null);
    props.setSaveShiftFinished(false);
  };

  // Handle opening the new shift modal
  const handleNewShiftClick = (dateStr) => {
    setNewShiftDate(dateStr);
    setNewShiftModalOpen(true);
  };

  // Handle closing the new shift modal
  const handleNewShiftModalClose = () => {
    setNewShiftModalOpen(false);
    setNewShiftDate(null);
    setSelectedEmployeeId(null);
    props.setSaveShiftFinished(false);
  };

  // Get formatted date string from date string
  const getFormattedDate = (dateStr) => {
    // Parse date string (format: "YYYY-MM-DD") to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    // Format as "Sun, Nov 16, 2025"
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Get day index relative to business week start
  const getDayIndex = (dateStr) => {
    // Parse date string as local date to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return (date.getDay() - businessWeekStartDayIndex + 7) % 7;
  };

  // Close modal when save is finished and re-fetch data
  useEffect(() => {
    if (props.saveShiftFinished && modalOpen) {
      // If the shift was a copilot suggestion, mark it as accepted
      if (selectedShift?.is_copilot_suggestion && selectedShift?.uuid) {
        setAcceptedCopilotShifts(prev => new Set([...prev, selectedShift.uuid]));
      }

      handleModalClose();
      // Re-fetch the monthly schedule to reflect the changes (without showing loading spinner)
      fetchMonthlySchedule(false);
    }
  }, [props.saveShiftFinished]);

  // Close new shift modal when save is finished and re-fetch data
  useEffect(() => {
    if (props.saveShiftFinished && newShiftModalOpen) {
      handleNewShiftModalClose();
      // Re-fetch the monthly schedule to reflect the changes (without showing loading spinner)
      fetchMonthlySchedule(false);
    }
  }, [props.saveShiftFinished, newShiftModalOpen]);

  // Re-fetch schedule when shift is saved/deleted outside of modal (e.g., X button)
  useEffect(() => {
    if (props.saveShiftFinished && !modalOpen && !newShiftModalOpen) {
      // Re-fetch the monthly schedule to reflect the changes
      fetchMonthlySchedule(false);
      props.setSaveShiftFinished(false);
    }
  }, [props.saveShiftFinished, modalOpen, newShiftModalOpen]);

  // Re-fetch events when an event is saved or deleted
  useEffect(() => {
    if (props.saveEventFinished) {
      // Re-fetch the monthly schedule (including events) to reflect the changes
      fetchMonthlySchedule(false);
    }
  }, [props.saveEventFinished]);

  // Assistant-confirmed mutations (day notes, shifts) arrive via the ops
  // refresh signal. The month view owns its own scheduleData/eventsData,
  // so HomeDisplay2's week/day refreshes never reach it — refetch here.
  const opsMonthlyRefreshRef = useRef(false);
  useEffect(() => {
    if (!opsMonthlyRefreshRef.current) {
      opsMonthlyRefreshRef.current = true;
      return;
    }
    if (!props.opsRefreshSignal) return;
    const types = props.opsRefreshSignal.types;
    if (types?.includes("events") || types?.includes("schedule")) {
      fetchMonthlySchedule(false);
    }
  }, [props.opsRefreshSignal]);

  // Re-fetch monthly schedule after shifts are published
  useEffect(() => {
    if (props.publishShiftsFinished) {
      fetchMonthlySchedule(false);
    }
  }, [props.publishShiftsFinished]);

  // Compute monthly unpublished/published counts and propagate to parent
  useEffect(() => {
    if (props.setMonthlyCountOfUnpublished && props.setMonthlyCountOfPublished) {
      const monthStart = formatDate(new Date(currentYear, currentMonth, 1));
      const monthEnd = formatDate(new Date(currentYear, currentMonth + 1, 0));

      const monthShifts = scheduleData?.filter(
        (shift) => shift.date >= monthStart && shift.date <= monthEnd && !shift.other_store
      ) || [];

      let unpublishedCount = 0;

      // Count shifts that have been deleted (published but no longer in schedule)
      if (monthShifts.length < monthlyPublishedCount) {
        unpublishedCount = monthlyPublishedCount - monthShifts.length;
      }

      // Count shifts marked as publishable (new or modified)
      monthShifts.forEach((shift) => {
        if (shift.is_publishable === true) {
          unpublishedCount += 1;
        }
      });

      props.setMonthlyCountOfUnpublished(unpublishedCount);
      props.setMonthlyCountOfPublished(monthlyPublishedCount);
    }
  }, [scheduleData, monthlyPublishedCount, currentMonth, currentYear]);

  return (
    <div className="w-full pt-2 pb-8">
      {/* Month Title and Back Button */}
      <div className="w-full flex justify-between items-center mb-4">
        <div></div> {/* Spacer for center alignment */}
        <div className="font-semibold text-2xl text-slate-900">
          {months[currentMonth]} {currentYear}
        </div>
        <div
          className="text-slate-500 hover:text-slate-900 flex items-center space-x-1 cursor-pointer"
          onClick={() => props.toggleDailyWeekly()}
        >
          <p className="text-xs">Weekly View</p>
          <div>
            <ArrowRightAltIcon />
          </div>
        </div>
      </div>

      {/* Day of Week Headers */}
      <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-slate-500 font-semibold ml-12">
        {daysOfWeek.map((dayOfWeek) => (
          <div key={dayOfWeek} className="text-center py-2">{dayOfWeek}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <LoadSpinner />
        </div>
      ) : (
        <div className="isolate grid grid-cols-7 gap-px bg-slate-200 text-sm shadow ring-1 ring-slate-200 rounded-lg overflow-visible relative ml-12">
          {days?.map((day, dayIdx) => {
            // Check if this is the first day of a week (for copilot button)
            const isFirstDayOfWeek = dayIdx % 7 === 0;
            const weekStartDate = isFirstDayOfWeek ? getWeekStartDate(day.date) : null;
            const isThisWeekCopilotActive = weekStartDate && isWeekCopilotActive(weekStartDate);
            // Check if week is in the past
            const weekEndDate = weekStartDate ? new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000) : null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const weekHasEnded = weekEndDate ? weekEndDate < today : false;
            // Show copilot buttons if the feature is enabled and the week hasn't ended
            const canShowCopilot = props.copilotEnabled && !weekHasEnded;

            return (
              <div
                key={dayIdx}
                className={classNames(
                  "min-h-[120px] p-2 group relative",
                  day.isCurrentMonth ? "bg-white" : "bg-slate-100"
                )}
              >
                {/* Copilot Button - Shows on first day of each week in the left margin */}
                {isFirstDayOfWeek && canShowCopilot && (() => {
                  // Get copilot shifts and deletion suggestions for this week
                  // Format week dates as strings for comparison (YYYY-MM-DD)
                  const weekStartStr = formatDate(weekStartDate);
                  const weekEndDate = new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000);
                  const weekEndStr = formatDate(weekEndDate);

                  const copilotShiftsThisWeek = props.all_data?.copilot_schedule?.filter(shift => {
                    if (!shift.is_copilot_suggestion) return false;
                    // Compare date strings directly (YYYY-MM-DD format)
                    return shift.date >= weekStartStr && shift.date <= weekEndStr && !acceptedCopilotShifts.has(shift.uuid);
                  }) || [];

                  const copilotDeleteShiftsThisWeek = props.all_data?.copilot_schedule?.filter(shift => {
                    if (!shift.copilot_suggested_deletion) return false;
                    // Compare date strings directly (YYYY-MM-DD format)
                    return shift.date >= weekStartStr && shift.date <= weekEndStr;
                  }) || [];

                  const hasRecommendations = copilotShiftsThisWeek.length > 0 || copilotDeleteShiftsThisWeek.length > 0;

                  return (
                    <div className="absolute -left-10 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
                      <Tooltip
                        title={
                          props.isCopilotLoading && isThisWeekCopilotActive
                            ? "Copilot calculating..."
                            : isThisWeekCopilotActive
                            ? "Hide copilot for this week"
                            : "Show copilot for this week"
                        }
                        placement="left"
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            console.log('Button clicked:', { weekStartDate, props });
                            handleWeekCopilotClick(weekStartDate);
                          }}
                          disabled={false}
                          sx={{
                            bgcolor: isThisWeekCopilotActive ? "#6d28d9" : "#f3f4f6",
                            color: isThisWeekCopilotActive ? "white" : "#6b7280",
                            "&:hover": {
                              bgcolor: isThisWeekCopilotActive ? "#5b21b6" : "#e5e7eb",
                            },
                            width: 32,
                            height: 32,
                          }}
                        >
                          {props.isCopilotLoading && isThisWeekCopilotActive ? (
                            <CircularProgress size={16} sx={{ color: "white" }} />
                          ) : (
                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Accept All Button - Only show when copilot is active and has recommendations */}
                      {isThisWeekCopilotActive && hasRecommendations && !props.isCopilotLoading && (
                        <Tooltip
                          title={`Accept all ${copilotShiftsThisWeek.length + copilotDeleteShiftsThisWeek.length} recommendations for this week`}
                          placement="left"
                        >
                          <button
                            onClick={() => {
                              // Prepare shifts for API call
                              const modifiedCopilotShifts = copilotShiftsThisWeek.map((shift) => ({
                                ...shift,
                                action: "add",
                              }));

                              const modifiedCopilotDeleteShifts = copilotDeleteShiftsThisWeek.map((shift) => ({
                                ...shift,
                                action: "delete",
                              }));

                              // Set loading state and call API
                              // addMultipleShifts will handle refetching and setting isAddingAllCopilotShiftsLoading back to false
                              props.setIsAddingAllCopilotShiftsLoading(true);
                              props.addMultipleShifts([
                                ...modifiedCopilotShifts,
                                ...modifiedCopilotDeleteShifts,
                              ]);
                            }}
                            disabled={props.isAddingAllCopilotShiftsLoading}
                            className="w-8 h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {props.isAddingAllCopilotShiftsLoading ? (
                              <CircularProgress size={14} sx={{ color: "white" }} />
                            ) : (
                              <CheckIcon sx={{ fontSize: 16 }} />
                            )}
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  );
                })()}

              {/* Day Number and Add Note */}
              <div className="mb-1 flex items-center justify-between group">
                <time
                  dateTime={day.date}
                  className={classNames(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                    day.isToday && "bg-violet-700 text-white",
                    !day.isToday && day.isCurrentMonth && "text-slate-900",
                    !day.isToday && !day.isCurrentMonth && "text-slate-400"
                  )}
                >
                  {day.dayNumber}
                </time>
                <div
                  className="text-[9px] px-1 py-0.5 text-slate-400 hover:text-violet-600 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5"
                  onClick={() => openNotesForDate(day.date)}
                  title="Add note"
                >
                  <AddIcon sx={{ fontSize: "10px" }} />
                  <span className="font-medium">Add Event / Note</span>
                </div>
              </div>

              {/* Holidays Section */}
              {day.holidays && day.holidays.length > 0 && (
                <div className="mb-2 space-y-1">
                  {day.holidays.map((holiday, holidayIdx) => (
                    <div
                      key={holiday.uuid ?? holidayIdx}
                      className="text-[9px] px-1.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded flex items-center gap-1"
                      title={holiday.name}
                    >
                      <EventOutlinedIcon sx={{ fontSize: "10px" }} />
                      <span className="truncate font-semibold flex-1">
                        {holiday.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes Section */}
              {day.events && day.events.length > 0 && (
                <div className="mb-2 space-y-1">
                  {day.events.slice(0, 2).map((event, eventIdx) => (
                    <div
                      key={eventIdx}
                      className="text-[9px] px-1.5 py-1 bg-white border border-violet-200 hover:border-violet-300 hover:bg-violet-50 text-violet-700 rounded cursor-pointer transition-colors flex items-center gap-1"
                      onClick={() => openNotesForDate(day.date)}
                      title={event.title || "Untitled note"}
                    >
                      <NoteAltOutlinedIcon sx={{ fontSize: "10px" }} />
                      <span className="truncate font-semibold flex-1">
                        {event.title && event.title.trim().length > 0
                          ? event.title
                          : "Untitled note"}
                      </span>
                      {event.attachments?.length > 0 && (
                        <AttachFileIcon sx={{ fontSize: "10px" }} className="shrink-0" />
                      )}
                    </div>
                  ))}
                  {day.events.length > 2 && (
                    <div
                      className="text-[9px] px-1.5 py-1 text-violet-600 hover:text-violet-700 cursor-pointer font-semibold"
                      onClick={() => openNotesForDate(day.date)}
                    >
                      +{day.events.length - 2} more
                    </div>
                  )}
                </div>
              )}

              {/* Shifts - Merged actual shifts and copilot suggestions, sorted by start time */}
              <div className="space-y-1">
                {/* Combine actual shifts with copilot suggestions and sort by start time */}
                {(() => {
                  const actualShifts = day.shifts.filter((shift) => {
                    // Exclude shifts from other stores
                    if (shift.other_store) {
                      return false;
                    }
                    // If no roles are selected, show all shifts
                    if (!props.selectedRolesForFiltering || props.selectedRolesForFiltering.length === 0) {
                      return true;
                    }
                    // Otherwise, only show shifts that match selected roles
                    return props.selectedRolesForFiltering.includes(shift.role_id);
                  });

                  const copilotShifts = getCopilotShiftsForDay(day.date).filter(shift => {
                    // Must be a copilot suggestion and not already accepted
                    if (!shift.is_copilot_suggestion || acceptedCopilotShifts.has(shift.uuid)) {
                      return false;
                    }
                    // Apply role filter (same logic as actual shifts)
                    if (!props.selectedRolesForFiltering || props.selectedRolesForFiltering.length === 0) {
                      return true;
                    }
                    return props.selectedRolesForFiltering.includes(shift.role_id);
                  });

                  const merged = [...actualShifts, ...copilotShifts];

                  if (day.date === '2026-01-23') {
                    console.log('Rendering shifts for 2026-01-23:', {
                      actualShiftsCount: actualShifts.length,
                      copilotShiftsCount: copilotShifts.length,
                      mergedCount: merged.length,
                      acceptedCopilotShiftsSize: acceptedCopilotShifts.size
                    });
                  }

                  // Stable sort: primary by shift_start, secondary by ID for consistent ordering
                  return merged.sort((a, b) => {
                    // First sort by start time
                    if (a.shift_start !== b.shift_start) {
                      return a.shift_start - b.shift_start;
                    }
                    // If same start time, maintain stable order using ID/uuid
                    const aId = a.uuid || a.id || 0;
                    const bId = b.uuid || b.id || 0;
                    return String(aId).localeCompare(String(bId));
                  });
                })()
                  .map((shift, idx) => {
                    // Check if this is a copilot suggestion
                    const isCopilotSuggestion = shift.is_copilot_suggestion;

                    // Render copilot suggestion shifts differently
                    if (isCopilotSuggestion) {
                      const dayInPast = isDateInPast(day.date);
                      const shiftColor = getCardColor2(
                        shift.role_id,
                        props.all_data?.role_names,
                        dayInPast,
                        true
                      );

                      const fullName = `${shift.first_name || ''} ${shift.last_name || ''}`.trim();
                      const displayLabel = shift.station?.name || shift.role_name || shift.role_type || '';

                      // Find person info for this shift
                      const person = props.store_team_member_weekly_info?.find(
                        (p) => p.id === shift.store_team_member
                      );

                      const handleLongPress = () => {
                        console.log('handleLongPress - copilot shift:', shift);

                        // Optimistically update the UI immediately
                        // 1. Remove from copilot shifts by marking as accepted
                        setAcceptedCopilotShifts(prev => new Set([...prev, shift.uuid]));

                        // 2. Add to actual schedule data immediately
                        // Mark as NOT a copilot suggestion (so it renders as solid, not faded)
                        // But use id: 0 instead of -1, so modal treats it as a new shift
                        const optimisticShift = {
                          ...shift,
                          is_copilot_suggestion: false, // Not a suggestion anymore (solid appearance)
                          id: 0, // Treat as new shift in modal (not -1 which causes errors)
                          uuid: shift.uuid // Keep original uuid for stable sorting
                        };

                        setScheduleData(prev => [...prev, optimisticShift]);

                        // Get the day of week for this shift's date
                        const [year, month, dayNum] = shift.date.split('-').map(Number);
                        const shiftDate = new Date(year, month - 1, dayNum);
                        const dayOfWeek = shiftDate.getDay();

                        // Calculate the index relative to business week start day
                        const dayIndex = (dayOfWeek - businessWeekStartDayIndex + 7) % 7;

                        // Create appliedTo array with only this day set to true
                        const appliedTo = [false, false, false, false, false, false, false];
                        appliedTo[dayIndex] = true;

                        // Calculate the week start date for this shift's week
                        const weekStartDate = getWeekStartDate(shift.date);

                        // Call addShift in the background to persist to server
                        // Pass the correct week start date for this shift's week (6th parameter)
                        // Use callback to refetch and replace optimistic shift with real shift from server
                        props.addShift(shift, "add", appliedTo, true, async () => {
                          // Refetch the monthly schedule to get the real shift with proper ID
                          await fetchMonthlySchedule(false);

                          // Check if the shift was actually added by looking for a shift on this date with the same person/times
                          // If not found, the API call failed - remove from acceptedCopilotShifts so copilot suggestion reappears
                          setScheduleData(currentData => {
                            const shiftExists = currentData.some(s =>
                              s.date === shift.date &&
                              s.store_team_member === shift.store_team_member &&
                              s.shift_start === shift.shift_start &&
                              s.shift_end === shift.shift_end &&
                              s.id !== -1 // Must have a real ID
                            );

                            if (!shiftExists) {
                              // Shift wasn't added successfully, unhide the copilot suggestion
                              setAcceptedCopilotShifts(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(shift.uuid);
                                return newSet;
                              });
                            }

                            return currentData;
                          });
                        }, weekStartDate);
                      };

                      const handleClick = () => {
                        if (person) {
                          // Create a stable deep copy of the shift for the modal
                          // This prevents the modal from resetting when copilot data refetches
                          // Keep the uuid so we can mark it as accepted on save
                          const shiftForModal = JSON.parse(JSON.stringify({
                            ...shift,
                            id: 0
                          }));
                          setSelectedShift(shiftForModal);
                          setSelectedPerson(person);
                          setSelectedDate(day.date);

                          // Initialize the employee selection for the edit modal
                          setLocalSelectedEmployeeId(person.id);

                          setModalOpen(true);
                        }
                      };

                      return (
                        <CopilotShiftCard
                          key={`copilot-${shift.uuid}`}
                          shift={shift}
                          shiftColor={shiftColor}
                          fullName={fullName}
                          displayLabel={displayLabel}
                          onLongPress={handleLongPress}
                          onClick={handleClick}
                          get_t_str={props.get_t_str}
                        />
                      );
                    }

                    // Render actual shifts
                    // Check if this shift is marked for deletion by copilot
                    // Only show deletion UI if copilot is active for this week
                    const shiftWeekStart = getWeekStartDate(shift.date);
                    const isCopilotActiveForThisWeek = isWeekCopilotActive(shiftWeekStart);
                    const isMarkedForDeletion = shift.copilot_suggested_deletion === true && isCopilotActiveForThisWeek;
                    const dayInPast = isDateInPast(day.date);
                    const hasClassifiedMissedShift = !!shift.publishedschedule?.missed_shift_reason;
                    const needsAttendanceReview =
                      dayInPast &&
                      shift.publishedschedule &&
                      shift.publishedschedule.has_actual_used_schedule === false &&
                      !shift.publishedschedule.clock_in_optional &&
                      !hasClassifiedMissedShift;
                    const shiftColor = getCardColor2(
                      shift.role_id,
                      props.all_data?.role_names,
                      dayInPast, // Use dayInPast for locked mode to get darker color
                      true
                    );

                    const fullName = `${shift.first_name || ''} ${shift.last_name || ''}`.trim();

                    // Determine what to show: station > role_name > role_type
                    const displayLabel = shift.station?.name || shift.role_name || shift.role_type || '';

                    return (
                      <div
                        key={idx}
                        className={`relative text-xs px-2 py-1 ${shiftColor} text-white rounded truncate shadow-sm cursor-pointer hover:opacity-90 transition-opacity ${
                          hasClassifiedMissedShift
                            ? "border-2 border-rose-500"
                            : needsAttendanceReview
                            ? "border-2 border-amber-400 border-dashed"
                            : isMarkedForDeletion
                            ? "opacity-50 border-2 border-dashed border-rose-400"
                            : ""
                        }`}
                        onClick={() => handleShiftClick(shift, day.date)}
                        title={isMarkedForDeletion ? "Copilot suggests removing this shift" : ""}
                      >
                        {/* Delete button for copilot suggested deletions */}
                        {isMarkedForDeletion && (
                          <Tooltip title="Copilot recommends deleting this shift - click X to delete">
                            <div
                              className="absolute -left-1 -top-1 w-4 h-4 rounded-full bg-slate-900 text-white hover:bg-slate-200 hover:text-slate-900 flex items-center justify-center z-10 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();

                                // Optimistically remove the shift from local state immediately
                                setScheduleData(prev => prev.filter(s => s.id !== shift.id));

                                // Calculate the day index for this shift
                                const dayIndex = getDayIndex(shift.date);
                                const appliedTo = [false, false, false, false, false, false, false];
                                appliedTo[dayIndex] = true;

                                // Calculate the week start date for this shift's week
                                const weekStartDate = getWeekStartDate(shift.date);

                                // Delete shift - pass the correct week start date for this shift's week (6th parameter)
                                // Use callback to refetch and ensure consistency (especially if delete fails)
                                props.addShift(shift, "delete", appliedTo, true, () => {
                                  // Refetch the monthly schedule to ensure UI matches server state
                                  fetchMonthlySchedule(false);
                                }, weekStartDate);
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 10 }} />
                            </div>
                          </Tooltip>
                        )}
                        <div className="flex items-center gap-1 font-semibold truncate text-white text-[10px] leading-tight">
                          {shift.store_team_member === null && shift.is_publishable === true && (
                            <Tooltip title="Unpublished. Not in the shift pool yet." arrow>
                              <span
                                aria-label="Unpublished"
                                className="inline-block w-[6px] h-[6px] rounded-full border-[1.5px] border-white shrink-0"
                              />
                            </Tooltip>
                          )}
                          <span className="truncate">
                            {fullName || (shift.store_team_member === null ? 'Open shift' : 'No Name')}
                          </span>
                        </div>
                        <div className="truncate text-white text-[9px] leading-snug">
                          {props.get_t_str(shift.shift_start)} - {props.get_t_str(shift.shift_end)}{shift.business_decline ? " (BD)" : ""} ({displayLabel})
                        </div>
                        {shift.note && (
                          <div className="absolute right-0.5 -top-0.5">
                            <Tooltip title={shift.note}>
                              <NoteAltIcon
                                sx={{ fontSize: "10px" }}
                                className="text-white"
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>

              {/* Add Shift Button */}
              {(!isDateInPast(day.date) || props.editShiftsInPastEnabled) && (
                <div className="mt-1 group-hover:block hidden">
                  <div
                    className="text-[9px] px-1.5 py-1 text-slate-400 hover:text-violet-600 hover:bg-violet-50 cursor-pointer rounded flex items-center gap-0.5 border border-dashed border-slate-300 hover:border-violet-300 transition-colors"
                    onClick={() => handleNewShiftClick(day.date)}
                    title="Add shift"
                  >
                    <AddIcon sx={{ fontSize: "10px" }} />
                    <span className="font-medium">Add Shift</span>
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {/* Shift Edit Modal */}
      {modalOpen && selectedShift && selectedDate && (() => {
        const wrappedAddShift = (shift_info, shift_save_type, appliedTo, outside_of_modal, onComplete) => {
          const weekStartDate = getWeekStartDate(selectedShift.date);
          props.addShift(shift_info, shift_save_type, appliedTo, outside_of_modal, onComplete, weekStartDate);
        };

        // Availability info will be calculated dynamically in the modal based on selected employee

        return (
          <ShiftInputsModal
            open={modalOpen}
            handleClose={handleModalClose}
            shift_info={selectedShift}
            shifts_info={[selectedShift]}
            p={selectedPerson}
            new_shift={selectedShift.is_copilot_suggestion ? true : false}
            addShift={wrappedAddShift}
            date_name={getFormattedDate(selectedDate)}
            availability_info={null}
            role_names={props.all_data?.role_names}
            getRoleName={(id) => props.all_data?.role_names?.find(r => r.id === id)?.role_name}
            getRoleType={(id) => props.all_data?.role_names?.find(r => r.id === id)?.role_type}
            saveShiftFinished={props.saveShiftFinished}
            setSaveShiftFinished={props.setSaveShiftFinished}
            d={getDayIndex(selectedDate)}
            role_types={props.all_data?.role_types}
            addMultipleShifts={props.addMultipleShifts}
            businessWeekStartDayIndex={businessWeekStartDayIndex}
            start_qhnum={props.start_qhnum}
            schedule_day_end_qhnum={props.schedule_day_end_qhnum}
            storeTimezone={props.storeTimezone}
            frequentShifts={props.frequentShifts}
            breakWaiverEnabled={props.breakWaiverEnabled}
            defaultBreakLengthHours={props.defaultBreakLengthHours}
            paidBreakInputsEnabled={props.paidBreakInputsEnabled}
            businessDeclineShiftOptionEnabled={props.businessDeclineShiftOptionEnabled}
            restBreakRegulation={props.restBreakRegulation}
            isMonthlyView={true}
            store_team_member_weekly_info={props.store_team_member_weekly_info}
            selectedEmployeeId={localSelectedEmployeeId}
            setSelectedEmployeeId={setLocalSelectedEmployeeId}
            existingShift={true}
            day_in_past={isDateInPast(selectedDate)}
            readOnly={
              isDateInPast(selectedDate) && !props.editShiftsInPastEnabled
            }
            post_missed_shift={props.post_missed_shift}
            onMissedShiftUpdated={() => fetchMonthlySchedule(false)}
          />
        );
      })()}

      {/* New Shift Modal (for adding shifts without pre-selected person) */}
      {newShiftModalOpen && newShiftDate && (() => {
        // Get availability info for selected employee
        const selectedEmployee = props.store_team_member_weekly_info?.find(emp => emp.id === selectedEmployeeId);
        const dayIdx = getDayIndex(newShiftDate);
        const availabilityInfo = selectedEmployee?.default_availability?.[dayIdx] || null;

        return (
          <ShiftInputsModal
            open={newShiftModalOpen}
            handleClose={handleNewShiftModalClose}
            shift_info={{
              adjustment_type: "no_adjust",
              break_length: 0,
              break_start: 12,
              break_length_2: 0,
              break_start_2: 0,
              date: newShiftDate,
              person: null,
              hours: 4,
              id: 0,
              role_id: null,
              role_type: null,
              station_id: null,
              station: null,
              shift_end: 16,
              shift_length: 4,
              shift_start: 12,
              store: props.store_id,
              store_team_member: null,
              note: null,
            }}
            shifts_info={[{
              adjustment_type: "no_adjust",
              break_length: 0,
              break_start: 12,
              break_length_2: 0,
              break_start_2: 0,
              date: newShiftDate,
              person: null,
              hours: 4,
              id: 0,
              role_id: null,
              role_type: null,
              station_id: null,
              station: null,
              shift_end: 16,
              shift_length: 4,
              shift_start: 12,
              store: props.store_id,
              store_team_member: null,
              note: null,
            }]}
            p={null}
            new_shift={true}
            addShift={(shift_info, shift_save_type, appliedTo, outside_of_modal, onComplete) => {
              const weekStartDate = getWeekStartDate(newShiftDate);
              props.addShift(shift_info, shift_save_type, appliedTo, outside_of_modal, onComplete, weekStartDate);
            }}
            date_name={getFormattedDate(newShiftDate)}
            availability_info={availabilityInfo}
            role_names={newShiftRoleNames}
            getRoleName={(id) => props.all_data?.role_names?.find(r => r.id === id)?.role_name}
            getRoleType={(id) => props.all_data?.role_names?.find(r => r.id === id)?.role_type}
            saveShiftFinished={props.saveShiftFinished}
            setSaveShiftFinished={props.setSaveShiftFinished}
            d={getDayIndex(newShiftDate)}
            role_types={props.all_data?.role_types}
            addMultipleShifts={props.addMultipleShifts}
            businessWeekStartDayIndex={businessWeekStartDayIndex}
            start_qhnum={props.start_qhnum}
            schedule_day_end_qhnum={props.schedule_day_end_qhnum}
            frequentShifts={props.frequentShifts}
            breakWaiverEnabled={props.breakWaiverEnabled}
            defaultBreakLengthHours={props.defaultBreakLengthHours}
            store_team_member_weekly_info={props.store_team_member_weekly_info}
            selectedEmployeeId={selectedEmployeeId}
            setSelectedEmployeeId={setSelectedEmployeeId}
            paidBreakInputsEnabled={props.paidBreakInputsEnabled}
            businessDeclineShiftOptionEnabled={props.businessDeclineShiftOptionEnabled}
            restBreakRegulation={props.restBreakRegulation}
            isMonthlyView={true}
          />
        );
      })()}

      {/* Notes Drawer */}
      {notesDrawerState.dateStr && (
        <WeatherEventsCard
          element="notes"
          daily_events={eventsData?.filter((event) => event.date === notesDrawerState.dateStr) || []}
          date_str={notesDrawerState.dateStr}
          saveEvent={props.saveEvent}
          updateEvent={props.updateEvent}
          deleteEvent={props.deleteEvent}
          eventAttachmentProps={props.eventAttachmentProps}
          saveEventFinished={props.saveEventFinished}
          setSaveEventFinished={props.setSaveEventFinished}
          openNotes={notesDrawerState.open}
          handleOpenNotes={() => {}}
          handleCloseNotes={closeNotesDrawer}
          setOpenNotes={setNotesDrawerOpen}
        />
      )}
    </div>
  );
}
